import {CrPersonIdentity} from '../../../../../../cr/logic/cr-person-identity';
import {casePersonCapturedIdentity, CwCasePersonNameSource} from '../../../../../util/cw-case-person-name.util';

/**
 * AI:
 * Status: "in progress"
 * Type: logic
 * SubType: check
 * Reason: J1 currency check — for every BOUND case person (personDtoId set), a direct pair comparison
 *         of the row's captured snapshot identity against its linked pm.Person's current stored
 *         names. No matcher, no similarity scoring, no CrPersonSuggestion: the pair is already known,
 *         so this is a plain equality check under the shared CrPersonIdentity normalisation.
 *
 * @ticket TM-1000001991
 */

/**
 * [J1 — Aktualitätsprüfung] Whether a case person's captured snapshot still agrees with the CURRENT
 * name of the `pm.Person` it links.
 *
 * <h3>Why this exists</h3>
 * `CwCasePersonOnChange` [CW-PERSON-IDENT] fills the still-empty snapshot columns from the linked
 * `pm.Person` on create AND on update, and — quoting it verbatim — "It also never overwrites, so a
 * hand-captured name always wins" (`CwCasePersonOnChange.java:100`). Nothing ever refreshes it
 * afterwards. If the person's name changes in the CRM later (marriage, a spelling correction), the
 * case keeps the OLD name forever, and that stale name is exactly what the BiPRO/TAA wire reads and
 * sends to the insurer: "The BiPRO/TAA wire reads those columns and only those"
 * (`CwCasePersonOnChange.java:121-123`). This file makes that drift VISIBLE. It does not fix it —
 * "refreshing" the snapshot is a named, deliberately deferred extension (capability-matrix X1)
 * precisely because it would change what goes out on that wire, which is a bigger decision than a
 * read-only check.
 *
 * <h3>Why this is NOT a matcher, and never will be</h3>
 * A sibling piece of work (J2, built elsewhere, NOT this file) answers "who is this human in the
 * customer's records?" for the ~139 UNLINKED case persons — a search problem, solved by
 * `cr-customer-persons` and its matcher (`CrPersonMatch`). This file answers a different question for
 * the ~1.068 LINKED ones: "does the row's own captured snapshot still agree with the person it
 * ALREADY links?" — a pair comparison of a pair that is not in doubt, never a search. Running a linked
 * row through the matcher was the diagnosed defect of an earlier, since-abandoned design
 * (capability-matrix Fassung 2, finding R1): the matcher answered "no match" for a linked row with a
 * merely stale snapshot, which is the wrong diagnosis for a pair the row itself already names. This
 * file has no access to a matcher, a candidate list or a similarity score, and per AUFLAGE A3 of the
 * signed capability matrix it never will — a bound row with a diverging snapshot gets its OWN result
 * status here, `DIVERGING`, and is never handed to `CrPersonMatch`.
 *
 * <h3>Where this file lives, and why</h3>
 * `cw` has exactly two conventions for Angular-free logic, and no existing `cw/logic/` folder anywhere
 * in the module (checked repo-wide while writing this file): logic SHARED across several screens goes
 * into `cw/util/*.util.ts` (`cw-case-person-name.util.ts`), and logic that belongs to exactly ONE step
 * sits flat, next to that step's component (`cw-case-object-step-persons.scope.ts`,
 * `cw-case-person-role-section.model.ts` — both in this very folder). J1's only host is the persons
 * step (capability-matrix, Host row) and it is not reused anywhere else the name ladder is, so it
 * follows the second pattern: flat, in this folder, beside its two existing neighbours. A new
 * `cw/logic/` was decided against for the same reason `PLAN.md` §2.2 already gives when it faced the
 * identical choice: it would be this module's first such folder, created for one file, when an
 * established pattern already fits without inventing anything.
 *
 * <p><b>What is NOT carried over from `PLAN.md`.</b> Its version of a file with this exact name mapped
 * `CwCasePersonDto` onto `CrPersonSuggestion` so a case row could be run through the `cr` matcher —
 * that was the OLD, since-superseded single-surface cut (capability-matrix Fassung 1/2, one area for
 * every row). Under the signed Fassung 4 split, J1 never touches `CrPersonSuggestion`, `CrPersonMatch`
 * or the matcher at all; only the FILE NAME (capability-matrix AUFLAGE A1) and the placement reasoning
 * above survive from the plan — not its content.
 *
 * <h3>What it deliberately does not do</h3>
 * <ul>
 *   <li><b>No write.</b> Every result is a read-only verdict for display; there is no persisted
 *       result either (FD-4) — a fresh page load re-derives it from scratch.</li>
 *   <li><b>No role, no label.</b> A row is a J1 candidate purely by carrying `personDtoId` — its
 *       `typeDtoId` / role slot is the persons step's own business and plays no part here.</li>
 *   <li><b>Exactly two fields.</b> `firstName` and `lastName` are the only 2 of the 19 deprecated
 *       snapshot columns the frontend DTO carries at all (`cw-case-person.dto.ts:44-60`). J1 is a
 *       NAMES-currency check, not an identity check, until the DTO is widened (capability-matrix,
 *       extension X2) — see the literal acceptance wording in AUFLAGE A5.</li>
 * </ul>
 *
 * <h3>[AUFLAGE A6 / S16, TM-1000001991] The customer-binding dimension — BUILT, and kept separate</h3>
 * J1 also answers S16: is the row's linked `pm.Person` actually bound to THIS customer? Measured on
 * dev/tenantA, **95 of 1.068** bound rows are not. That question is ORTHOGONAL to the name comparison —
 * a row can carry a perfectly current name ({@link CwCasePersonCheckStatus} `MATCHING`) and still be
 * bound to a foreign customer, or the other way round. Folding it into
 * {@link CwCasePersonCheckStatus} would repeat a conflation this feature has already been corrected out
 * of three times (Fassung 1 comparing a person with itself, Fassung 2 running a linked row through the
 * matcher): a single union value could only ever report one of the two, and it would lose the other on
 * exactly the row that has both problems. So it is NOT a fourth `CwCasePersonCheckStatus` value — it is
 * {@link CwCasePersonCheckResult.customerBinding}, a SEPARATE, independent field on the same result,
 * and both statements are rendered side by side.
 *
 * <p><b>This file still reads nothing.</b> The person ids around the customer are handed IN, as
 * {@link CwCasePersonCustomerMatrixOutcome}, by the caller that owns the read
 * (`CrPersonMatrixService.getMatrix`, reused read-only — `setCustomerId` is never called, see the
 * component). That keeps this file Angular-free and pure, and it is what makes the three states
 * "never asked" / "read, possibly empty" / "read failed" DISTINGUISHABLE: a bare `Set` could not tell
 * an empty matrix from a failed one, and would have reported every bound row of a broken read as
 * customer-foreign.
 *
 * <p><b>A failed read is SHOWN, never swallowed</b> (AUFLAGE A6): every bound row is marked
 * {@link CwCasePersonCustomerBindingStatus} `NOT_VERIFIABLE` and carries
 * {@link CW_CASE_PERSON_CHECK_NOT_VERIFIABLE_MESSAGE}. Silence there would make a customer-foreign
 * binding look unremarkable, which is the exact reason S16 was reinstated.
 *
 * <p>The verdict is an ANNOTATION, not a gate: J1 writes nothing at all, so nothing is blocked by it.
 *
 * <h3>{@link CwCasePersonCheckStatus} below is the NAME comparison, and nothing else — pin this down
 * before touching it</h3>
 * That union describes EXCLUSIVELY whether the captured snapshot agrees with the linked person's
 * current name. The customer-binding question just described — capability-matrix **S16**, "die
 * gebundene Person hängt gar nicht am Kunden" — is an ORTHOGONAL dimension and must NEVER become a
 * fourth member of that union, for the reason spelled out above. It lives on its own field with three
 * states of its own — bound to the customer (`undefined`), not bound, not verifiable.
 */
export type CwCasePersonCheckStatus =

    /** The captured snapshot and the linked person's current name agree, under the shared normalisation. */
    | 'MATCHING'

    /**
     * The captured snapshot and the linked person's current name DISAGREE. See
     * {@link CW_CASE_PERSON_CHECK_DIVERGING_MESSAGE} for the exact, unparaphrased wording this status
     * always carries as its {@link CwCasePersonCheckResult.message}.
     */
    | 'DIVERGING'

    /**
     * The row carries no captured snapshot at all (both `firstName` and `lastName` normalise to
     * `null`) — there is nothing to compare the linked person against. This is its OWN state and is
     * never reported as `DIVERGING`: an empty snapshot is not evidence of a wrong one.
     */
    | 'NO_CAPTURED_IDENTITY';

/**
 * The exact German sentence AUFLAGE A3 of the signed capability matrix fixes for a `DIVERGING` row.
 * Rendered verbatim wherever it is shown — never re-worded, never composed from parts, the same house
 * rule `CrPersonDiscrepancy.message` and `CrPersonSuggestionParseResult.errors` already follow.
 *
 * @ticket TM-1000001991
 */
export const CW_CASE_PERSON_CHECK_DIVERGING_MESSAGE =
    'Der im Vorgang erfasste Stand weicht von der verknüpften Person ab.';

/**
 * [AUFLAGE A6 / S16] The two possible verdicts of the customer-binding check — see
 * {@link CwCasePersonCheckResult.customerBinding} for how this relates to (and is kept SEPARATE from)
 * {@link CwCasePersonCheckStatus}.
 *
 * <p>Deliberately only TWO values, not three: the ordinary case — the person IS bound to the case's
 * customer, or the check was not asked for at all — is represented by
 * {@link CwCasePersonCheckResult.customerBinding} being `undefined`, the same "absence means nothing
 * to report" convention `CrPersonDiscrepancy` arrays already use. A value here is populated ONLY for
 * the two cases the processor needs to be told about. Which of the two meanings an `undefined`
 * carries is not this field's business and must not be guessed from it: the CALLER knows whether it
 * passed a {@link CwCasePersonCustomerMatrixOutcome}, and only the caller may render "zugeordnet".
 *
 * @ticket TM-1000001991
 */
export type CwCasePersonCustomerBindingStatus =

    /** S16: the matrix read succeeded and the row's linked `pm.Person` is NOT in it. */
    | 'NOT_BOUND_TO_CUSTOMER'

    /**
     * The matrix read FAILED. Must be shown, never silently dropped — a silent failure here would make
     * a customer-foreign binding look like an unremarkable one, undoing the exact reason S16 was
     * reinstated. See {@link CW_CASE_PERSON_CHECK_NOT_VERIFIABLE_MESSAGE} for the fixed wording.
     */
    | 'NOT_VERIFIABLE';

/**
 * [AUFLAGE A6] The exact German sentence for {@link CwCasePersonCustomerBindingStatus}
 * `NOT_VERIFIABLE`, fixed the same way AUFLAGE A3 fixed
 * {@link CW_CASE_PERSON_CHECK_DIVERGING_MESSAGE}: rendered verbatim, never re-worded, never composed
 * from parts.
 *
 * @ticket TM-1000001991
 */
export const CW_CASE_PERSON_CHECK_NOT_VERIFIABLE_MESSAGE = 'Zuordnung zum Kunden nicht prüfbar.';

/**
 * [S16] The German sentence for {@link CwCasePersonCustomerBindingStatus} `NOT_BOUND_TO_CUSTOMER`.
 *
 * <p>A STATEMENT, not an instruction: this file has no opinion on what should happen next, and neither
 * does the screen — J1 writes nothing. Whether the case, the person or the binding is the wrong one is a
 * judgement only the processor can make, so the wording says what was measured and stops there.
 *
 * @ticket TM-1000001991
 */
export const CW_CASE_PERSON_CHECK_NOT_BOUND_MESSAGE = 'Diese Person ist dem Kunden nicht zugeordnet.';

/**
 * [S16] What the CALLER found out about the case customer's people — the input of the customer-binding
 * check, handed in rather than read here.
 *
 * <h3>Why a tagged union and not a bare `Set`</h3>
 * Three states have to stay apart, and two of them look identical through a `Set`:
 * <ul>
 *   <li><b>not asked</b> — the parameter is absent. Every {@link CwCasePersonCheckResult.customerBinding}
 *       stays `undefined`; this is the behaviour of a caller that has no customer, or does not want the
 *       check.</li>
 *   <li><b>`loaded`</b> — the matrix was read. `personIds` may legitimately be EMPTY (a customer whose
 *       matrix carries no person columns), and an empty matrix is a real answer: every bound row is then
 *       genuinely customer-foreign.</li>
 *   <li><b>`failed`</b> — the read did not succeed. We know NOTHING, and saying nothing would be a
 *       lie by omission (AUFLAGE A6).</li>
 * </ul>
 * A bare `Set` collapses the last two into "empty", which would report every bound row of a broken read
 * as `NOT_BOUND_TO_CUSTOMER` — a false accusation on 100% of the rows, produced by an infrastructure
 * hiccup. That is why this type exists.
 *
 * @ticket TM-1000001991
 */
export type CwCasePersonCustomerMatrixOutcome =

    /** The customer's people, by `pm.Person.id`. An EMPTY set is a valid, meaningful answer. */
    | { status: 'loaded'; personIds: ReadonlySet<number> }

    /** The read failed; nothing is known about any row's binding. */
    | { status: 'failed' };

/**
 * ONE case person's J1 verdict. See the file doc for what each {@link status} means.
 *
 * @ticket TM-1000001991
 */
export interface CwCasePersonCheckResult {

    /**
     * The row's OPAQUE identity — `String(casePerson.id)`, unchanged, never parsed. The J1 analogue of
     * `CrPersonSuggestion.reference` ("THIS SEAM IS THE POINT", `cr-person-suggestion.ts:28`):
     * whatever a caller can recognise its own row by, and nothing this file ever reads meaning out of.
     */
    reference: string;

    status: CwCasePersonCheckStatus;

    /** The exact wording of {@link CW_CASE_PERSON_CHECK_DIVERGING_MESSAGE} for `DIVERGING`; `undefined` otherwise. */
    message?: string;

    /** The row's captured snapshot, as read by `casePersonCapturedIdentity` — for display, un-normalised. */
    capturedFirstName?: string;
    capturedLastName?: string;

    /** The linked `pm.Person`'s CURRENT stored names — for display, un-normalised. */
    personFirstName?: string;
    personLastName?: string;

    /**
     * [AUFLAGE A6 / S16] The customer-binding verdict, ORTHOGONAL to {@link status}: a row can be
     * `MATCHING` on its name and still carry a customer-foreign binding, or the other way round — the
     * two are independent questions and must never be folded into one field (see the file doc).
     *
     * <p>`undefined` means one of TWO things, and this field cannot tell them apart: the row IS bound
     * to the customer, or the check was not asked for (no
     * {@link CwCasePersonCustomerMatrixOutcome} was handed to
     * {@link checkCaseCapturedIdentities}). The caller knows which, and only the caller may render a
     * positive "zugeordnet" — deriving one from `undefined` alone would claim a check that never ran.
     */
    customerBinding?: CwCasePersonCustomerBindingStatus;

    /**
     * The German wording of {@link customerBinding} — {@link CW_CASE_PERSON_CHECK_NOT_BOUND_MESSAGE} or
     * {@link CW_CASE_PERSON_CHECK_NOT_VERIFIABLE_MESSAGE}; `undefined` when {@link customerBinding} is.
     *
     * <p>Its own field rather than folded into {@link message}: that one carries the NAME finding, and
     * a row can carry both at once. Concatenating them would let one statement hide the other, which is
     * precisely what the orthogonality of the two dimensions forbids.
     */
    customerBindingMessage?: string;
}

/**
 * The minimum shape J1 reads off a case person — deliberately structural, like
 * `CwCasePersonNameSource`, so a caller can hand in a `CwCasePersonDto`, a QL projection or a
 * hand-built test fixture without a cast.
 *
 * @ticket TM-1000001991
 */
export interface CwCasePersonCheckSource extends CwCasePersonNameSource {

    /** `DtoDetail.id` of the row — the source of {@link CwCasePersonCheckResult.reference}. */
    id: number;

    /**
     * FK onto the linked `pm.Person`. MUST be read with `typeof personDtoId === 'number'`, never
     * truthiness: `CwCasePersonDto.personDtoId` is typed non-optional `number` today even though the
     * entity is nullable by design (capability-matrix, Befund C1.7), so an absent link still has to be
     * guarded against at the type level here.
     */
    personDtoId?: number | null;

    /** The linked `pm.Person`'s CURRENT stored names — the "today" half of the pair J1 compares. */
    personDto?: {
        firstName?: string | null;
        lastName?: string | null;
    } | null;
}

/**
 * The J1 verdict for ONE case person, ASSUMING it is bound. Callers with a mixed list of rows use
 * {@link checkCaseCapturedIdentities} instead, which applies the row-selection rule first.
 *
 * @ticket TM-1000001991
 * @param row a case person row; its `personDtoId` is not re-checked here
 * @return the row's J1 verdict
 */
export function checkCaseCapturedIdentity(row: CwCasePersonCheckSource): CwCasePersonCheckResult {

    // TM-1000001991: read the two captured columns ONLY through casePersonCapturedIdentity — this file
    // must never become a second reader of CwCasePersonDto.firstName/.lastName (cw-case-person.dto.ts:40-41).
    const captured = casePersonCapturedIdentity(row);
    const person = row.personDto ?? undefined;

    const reference = String(row.id);
    // captured.firstName/.lastName are already `string | undefined` (casePersonCapturedIdentity's own
    // return type); only the personDto side needs null -> undefined, since it may carry either.
    const capturedFirstName = captured.firstName;
    const capturedLastName = captured.lastName;
    const personFirstName = person?.firstName ?? undefined;
    const personLastName = person?.lastName ?? undefined;

    // AUFLAGE A3: the shared, case-insensitive/trimming normalisation only — never a similarity score.
    const normalisedCapturedFirstName = CrPersonIdentity.normalise(captured.firstName);
    const normalisedCapturedLastName = CrPersonIdentity.normalise(captured.lastName);

    if (normalisedCapturedFirstName == null && normalisedCapturedLastName == null) {
        return {
            reference,
            status: 'NO_CAPTURED_IDENTITY',
            capturedFirstName,
            capturedLastName,
            personFirstName,
            personLastName,
        };
    }

    const normalisedPersonFirstName = CrPersonIdentity.normalise(person?.firstName);
    const normalisedPersonLastName = CrPersonIdentity.normalise(person?.lastName);

    const matches = normalisedCapturedFirstName === normalisedPersonFirstName
        && normalisedCapturedLastName === normalisedPersonLastName;

    return {
        reference,
        status: matches ? 'MATCHING' : 'DIVERGING',
        message: matches ? undefined : CW_CASE_PERSON_CHECK_DIVERGING_MESSAGE,
        capturedFirstName,
        capturedLastName,
        personFirstName,
        personLastName,
    };
}

/**
 * The J1 verdicts for every BOUND row of a case — the row-selection rule from the signed capability
 * matrix, applied ONCE so no caller re-derives it.
 *
 * <p>`typeof row.personDtoId === 'number'`, deliberately NEVER truthiness (Befund C1.7): a `0` would be
 * falsy and therefore silently dropped by a truthiness check, even though `0` is a legal, real primary
 * key. An UNLINKED row (`personDtoId` absent) is not a J1 result of any status — it is simply not in
 * this list. J1 has nothing to say about it; that row belongs to J2, built elsewhere.
 *
 * <h3>[S16 / AUFLAGE A6] The optional {@link customerMatrix} — the SECOND, orthogonal question</h3>
 * It rides on THIS function and deliberately not on {@link checkCaseCapturedIdentity}: the binding
 * question is asked of the case as a whole (one matrix read for all rows), and putting a
 * whole-case parameter on a single-row function would invite a caller to pass a different answer per
 * row. The single-row function therefore has no access to it and never will.
 *
 * <ul>
 *   <li><b>omitted</b> — no binding verdict at all; every {@link CwCasePersonCheckResult.customerBinding}
 *       stays `undefined`, which is the behaviour of every caller written before S16;</li>
 *   <li><b>`loaded`</b> — a row whose linked person is NOT in `personIds` gets
 *       `NOT_BOUND_TO_CUSTOMER`; one that IS in it gets `undefined`, the "nothing to report" value;</li>
 *   <li><b>`failed`</b> — EVERY bound row gets `NOT_VERIFIABLE`. Never silently skipped: AUFLAGE A6
 *       exists because a swallowed failure makes a customer-foreign binding look unremarkable.</li>
 * </ul>
 *
 * <p>The verdict never touches {@link CwCasePersonCheckResult.status} or
 * {@link CwCasePersonCheckResult.message}. The two dimensions are independent and both are reported.
 *
 * @ticket TM-1000001991
 * @param casePersons   the case's persons, in any order; `null`/`undefined` yields an empty result
 * @param customerMatrix what the caller found out about the customer's people; omit to skip S16
 * @return one {@link CwCasePersonCheckResult} per BOUND row, in the input's order
 */
export function checkCaseCapturedIdentities(
    casePersons: readonly CwCasePersonCheckSource[] | null | undefined,
    customerMatrix?: CwCasePersonCustomerMatrixOutcome | null,
): CwCasePersonCheckResult[] {

    return (casePersons ?? [])
        .filter(row => typeof row.personDtoId === 'number')
        .map(row => withCustomerBinding(checkCaseCapturedIdentity(row), row, customerMatrix));
}

/**
 * [S16] Annotates ONE already-computed name verdict with the customer-binding verdict.
 *
 * <p>Returns the result unchanged when there is nothing to say — no matrix handed in, or the row's
 * person is in it. Never mutates {@link CwCasePersonCheckResult.status} or `.message`.
 *
 * @ticket TM-1000001991
 */
function withCustomerBinding(result: CwCasePersonCheckResult,
                             row: CwCasePersonCheckSource,
                             customerMatrix: CwCasePersonCustomerMatrixOutcome | null | undefined,
): CwCasePersonCheckResult {

    if (customerMatrix == null) {
        return result;
    }

    if (customerMatrix.status === 'failed') {
        // AUFLAGE A6: a failed read is stated on every affected row, never swallowed.
        return {
            ...result,
            customerBinding: 'NOT_VERIFIABLE',
            customerBindingMessage: CW_CASE_PERSON_CHECK_NOT_VERIFIABLE_MESSAGE,
        };
    }

    // `typeof === 'number'` again rather than a non-null assertion: this function is reachable only
    // through the filter above today, but the guarantee should live where the value is read.
    const personId = row.personDtoId;

    if (typeof personId !== 'number' || customerMatrix.personIds.has(personId)) {
        return result;
    }

    return {
        ...result,
        customerBinding: 'NOT_BOUND_TO_CUSTOMER',
        customerBindingMessage: CW_CASE_PERSON_CHECK_NOT_BOUND_MESSAGE,
    };
}
